import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { useAssessment } from "../context/AssessmentContext";
import { toast } from "sonner";
import { 
  ArrowRight, 
  Users, 
  Target, 
  BarChart3, 
  Sparkles,
  UserPlus,
  Crown,
  PlayCircle,
  RefreshCw,
  Loader2
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { 
    joinSession, 
    createSession, 
    loading, 
    initializing,
    member, 
    session, 
    user,
    progress,
    clearSession 
  } = useAssessment();
  
  const [activeTab, setActiveTab] = useState("join");
  
  // Join form
  const [inviteCode, setInviteCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  
  // Create form
  const [sessionName, setSessionName] = useState("");
  const [facilitatorName, setFacilitatorName] = useState("");
  const [facilitatorEmail, setFacilitatorEmail] = useState("");

  // Check if user has an existing session
  const hasExistingSession = member && session && user;
  const isSubmitted = member?.submitted_at || member?.status === "submitted";

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim() || !displayName.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      const result = await joinSession(inviteCode.trim(), displayName.trim());
      if (result.is_returning) {
        toast.success("Welcome back! Your progress has been restored.");
      } else {
        toast.success("Welcome! Let's begin your assessment");
      }
      navigate("/assessment");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!sessionName.trim() || !facilitatorName.trim()) {
      toast.error("Please fill in required fields");
      return;
    }
    
    try {
      const newSession = await createSession(
        sessionName.trim(),
        facilitatorName.trim(),
        facilitatorEmail.trim() || null
      );
      toast.success("Session created successfully!");
      navigate(`/facilitator/${newSession.id}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleContinue = () => {
    if (isSubmitted) {
      navigate("/review");
    } else {
      navigate("/assessment");
    }
  };

  const handleStartNew = () => {
    clearSession();
    toast.success("Session cleared. You can now join a new session.");
  };

  // Show loading while restoring session
  if (initializing) {
    return (
      <div className="min-h-screen safari-gradient flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen safari-gradient" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              Life Wheel Assessment
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              Discover Balance,
              <br />
              <span className="text-primary">Unlock Potential</span>
            </h1>
            
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              A reflective self-assessment tool designed for Families, EO, & YPO forums. 
              Rate eight key life domains, capture meaningful insights, 
              and commit to growth over the next 12 months.
            </p>
            
            <p className="text-xs text-muted-foreground/70 mt-4">
              This app is not associated with EO or YPO
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="card-hover border-0 shadow-sm bg-white/80 backdrop-blur">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">8 Life Domains</h3>
                <p className="text-sm text-muted-foreground">
                  Comprehensive assessment across physical, emotional, family, social, financial, career, intellectual, and spiritual areas.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-hover border-0 shadow-sm bg-white/80 backdrop-blur">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Team Insights</h3>
                <p className="text-sm text-muted-foreground">
                  Share results with your forum for powerful group discussions and collective accountability.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-hover border-0 shadow-sm bg-white/80 backdrop-blur">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Visual Analytics</h3>
                <p className="text-sm text-muted-foreground">
                  Interactive radar charts and heat maps reveal patterns and areas for focused growth.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Continue Assessment Card (if user has existing session) */}
          {hasExistingSession && (
            <Card className="max-w-md mx-auto border-0 shadow-lg bg-white mb-6" data-testid="continue-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl">Welcome Back, {user.display_name}!</CardTitle>
                <CardDescription>
                  {isSubmitted 
                    ? "Your assessment has been submitted. View your results." 
                    : "Continue where you left off"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">{session.name}</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Code: {session.invite_code}
                  </p>
                  
                  {progress?.overall && !isSubmitted && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{progress.overall.completed}/{progress.overall.total} items</span>
                      </div>
                      <Progress value={progress.overall.percentage} className="h-2" />
                    </div>
                  )}
                  
                  {isSubmitted && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Assessment Submitted
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleContinue} 
                  className="w-full"
                  data-testid="btn-continue"
                >
                  <PlayCircle className="mr-2 h-4 w-4" />
                  {isSubmitted ? "View Results" : "Continue Assessment"}
                </Button>
                
                <Button 
                  variant="ghost" 
                  onClick={handleStartNew} 
                  className="w-full text-muted-foreground"
                  data-testid="btn-start-new"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Start New Session
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Join/Create Form */}
          {!hasExistingSession && (
            <Card className="max-w-md mx-auto border-0 shadow-lg bg-white" data-testid="auth-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl">Get Started</CardTitle>
                <CardDescription>
                  Join an existing session or create a new one
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="join" data-testid="tab-join">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Join Session
                    </TabsTrigger>
                    <TabsTrigger value="create" data-testid="tab-create">
                      <Crown className="h-4 w-4 mr-2" />
                      Facilitate
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="join">
                    <form onSubmit={handleJoin} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Invite Code
                        </label>
                        <Input
                          placeholder="Enter 8-character code"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                          maxLength={8}
                          className="text-center text-lg tracking-wider uppercase"
                          data-testid="input-invite-code"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Your Name
                        </label>
                        <Input
                          placeholder="How should we call you?"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          data-testid="input-display-name"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Use the same name to continue a previous assessment
                        </p>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={loading}
                        data-testid="btn-join"
                      >
                        {loading ? "Joining..." : "Join Assessment"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="create">
                    <form onSubmit={handleCreate} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Session Name *
                        </label>
                        <Input
                          placeholder="e.g., Safari Forum Q1 2026"
                          value={sessionName}
                          onChange={(e) => setSessionName(e.target.value)}
                          data-testid="input-session-name"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Your Name *
                        </label>
                        <Input
                          placeholder="Facilitator name"
                          value={facilitatorName}
                          onChange={(e) => setFacilitatorName(e.target.value)}
                          data-testid="input-facilitator-name"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Email <span className="text-muted-foreground">(optional)</span>
                        </label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={facilitatorEmail}
                          onChange={(e) => setFacilitatorEmail(e.target.value)}
                          data-testid="input-facilitator-email"
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={loading}
                        data-testid="btn-create"
                      >
                        {loading ? "Creating..." : "Create Session"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-sm text-muted-foreground">
        <p>Life Wheel Assessment • Designed for YPO Global One Safari Forum</p>
      </footer>
    </div>
  );
}
